<template>
  <!-- <PersonAvatar>: the one circle for a person everywhere (spec §7): initials, the ring says how they stand, one dot says the most important extra -->
  <span :role="clickable ? 'button' : 'img'" :aria-label="ariaLabel" :title="ariaLabel" :tabindex="clickable ? 0 : undefined"
    class="relative inline-grid place-items-center shrink-0 rounded-full font-bold select-none"
    @click="openPerson" @keydown.enter.space="openPerson"
    :class="[clickable && 'cursor-pointer hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2', SIZE[size].box, team ? 'bg-lilac text-ink' : 'bg-ash-2 text-ink dark:bg-surface-700 dark:text-surface-0', ringClass]">
    <span aria-hidden="true">{{ initials }}</span>
    <span v-if="dot" aria-hidden="true"
      class="absolute -right-1.5 -bottom-1.5 rounded-full grid place-items-center font-extrabold leading-none ring-2 ring-white dark:ring-surface-900"
      :class="[SIZE[size].dot, DOT[dot]]">
      <!-- Binnen: a drawn check, so it reads at every size, also at 24 where the glyphs are too small -->
      <svg v-if="dot === 'in'" viewBox="0 0 10 10" class="size-[70%]" fill="none" stroke="currentColor" stroke-width="1.6"
        stroke-linecap="round" stroke-linejoin="round"><path d="M2.4 5.3 4.3 7.1 7.7 3.2" /></svg>
      <template v-else>{{ size > 24 ? glyph : '' }}</template>
    </span>
  </span>
</template>

<script lang="ts" setup>
const props = withDefaults(defineProps<{
  name?: string | null;
  email?: string | null;
  team?: boolean;
  ring?: 'ticket' | 'waiting' | 'refunded';
  dot?: 'in' | 'offered' | 'guest' | 'returning';
  // How many times this address came: shown in the `returning` dot
  visits?: number;
  size?: 24 | 36 | 56;
  // Replaces the whole spoken label
  label?: string;
  // Not clickable: where the person's card is already open
  static?: boolean;
}>(), { size: 36 });

// Anywhere a visitor's avatar shows, clicking it opens their contact card (usePersonDialog)
const userStore = useUserStore();
const clickable = computed(() => !!props.email && !props.team && !props.static && userStore.hasRight('mail.read'));
const { open } = usePersonDialog();
const openPerson = (e: Event) => {
  if (!clickable.value) return;
  e.stopPropagation();
  e.preventDefault();
  open(props.email!);
};

const SIZE = {
  24: { box: 'size-6 text-[10px]', dot: 'size-2.5 text-[0px]' },
  36: { box: 'size-9 text-[13px]', dot: 'size-[15px] text-[8px]' },
  56: { box: 'size-14 text-lg', dot: 'size-[22px] text-[11px]' },
} as const;

const DOT = {
  in: 'bg-ink text-white dark:bg-surface-0 dark:text-ink',
  offered: 'bg-signal text-ink',
  guest: 'bg-ice text-petrol',
  returning: 'bg-ink text-white dark:bg-surface-0 dark:text-ink',
} as const;

// The team is lilac and never gets a ring (sheet "Bezoekers")
const ringClass = computed(() => {
  if (props.team || !props.ring) return '';
  return {
    // State by shape, not colour: a calm ink ring, dashed while waiting
    ticket: 'outline outline-2 outline-offset-2 outline-ink dark:outline-surface-0',
    waiting: 'outline outline-2 outline-offset-2 outline-dashed outline-ink dark:outline-surface-0',
    refunded: 'opacity-[.45]',
  }[props.ring];
});

const glyph = computed(() => ({
  in: '', offered: '!', guest: '★', returning: props.visits && props.visits < 100 ? String(props.visits) : '',
}[props.dot!]));

// A name that is really an address or a domain says nothing: use the email then
const looksLikeAddress = (s: string) => s.includes('@') || /^\S+\.[a-z]{2,}$/i.test(s);
const firstLetter = (s?: string) => Array.from(s ?? '').find(c => /\p{L}|\p{N}/u.test(c))?.toUpperCase() ?? '';

const initials = computed(() => {
  const name = props.name?.trim();
  if (name && !looksLikeAddress(name)) {
    const words = name.split(/\s+/);
    return firstLetter(words[0]) + (words.length > 1 ? firstLetter(words[words.length - 1]) : '');
  }
  return firstLetter(props.email?.trim()) || '?';
});

const { t } = useI18n();

const ariaLabel = computed(() => {
  if (props.label) return props.label;
  const who = props.name?.trim() || props.email?.trim() || t('avatar_unknown');
  const state = [
    props.team && t('avatar_team'),
    !props.team && props.ring && t(`avatar_${props.ring}`),
    props.dot === 'returning' && props.visits ? t('avatar_visits', { n: props.visits }) : props.dot && t(`avatar_${props.dot}`),
  ].filter(Boolean);
  return [who, ...state].join(', ');
});
</script>
